'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useDarkMode } from '@/components/DarkModeProvider';
import { useAuth } from '@/contexts/AuthContext';
import Footer from '@/components/Footer';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import ErrorBoundary from '@/components/ErrorBoundary';
import { TemplateCardSkeleton } from '@/components/ui/SkeletonLoader';
import { supabase } from '@/lib/supabaseClient';
import { useToast } from '@/contexts/ToastContext';
import { trackWishlistRemove } from '@/lib/analytics';

interface WishlistItem {
  id: string;
  template_id: string;
  created_at: string;
  template: {
    id: string;
    name: string;
    platform: string;
    category: string | null;
    price: number;
    rating: number;
    reviews_count: number;
    downloads_count: number;
    thumbnail_url: string | null;
    tags: string[];
  } | null;
}

const platformColors: Record<string, string> = {
  shopify: '#96BF48',
  wordpress: '#21759B',
  woocommerce: '#7F54B3',
  html: '#E8633A',
  nextjs: '#000000',
  webflow: '#4353FF',
};

function StarRating({ rating, reviews }: { rating: number; reviews: number }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((s) => (
        <Icon
          key={s}
          name="StarIcon"
          size={11}
          variant={s <= Math.floor(rating) ? 'solid' : 'outline'}
          className={s <= Math.floor(rating) ? 'star-filled' : 'star-empty'}
        />
      ))}
      <span className="text-xs ml-1" style={{ color: 'var(--text-muted)' }}>
        {rating} ({reviews})
      </span>
    </div>
  );
}

export default function WishlistClient() {
  return (
    <ErrorBoundary componentName="Wishlist">
      <WishlistContent />
    </ErrorBoundary>
  );
}

function WishlistContent() {
  const { darkMode, toggleDarkMode } = useDarkMode();
  const { user, loading: authLoading } = useAuth();
  const { showSuccess, showError } = useToast();
  const router = useRouter();
  const [wishlistItems, setWishlistItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && !user) {
      router.push('/sign-up-login');
      return;
    }
    if (user) {
      loadWishlist();
    }
  }, [user, authLoading]);

  const loadWishlist = async () => {
    try {
      setLoading(true);
      // Try to fetch from wishlists table with template join
      const { data, error } = await supabase
        .from('wishlists')
        .select('id, template_id, created_at, template:templates(id, name, platform, category, price, rating, reviews_count, downloads_count, thumbnail_url, tags)')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false });

      if (error) {
        // If table doesn't exist, show empty state gracefully
        if (error.code === '42P01' || error.message?.includes('does not exist')) {
          console.warn('[Wishlist] wishlists table not found, showing empty state');
          setWishlistItems([]);
        } else {
          console.error('[Wishlist] fetch error:', error);
          showError('Failed to load wishlist');
        }
      } else {
        setWishlistItems((data as WishlistItem[]) || []);
      }
    } catch (err) {
      console.error('[Wishlist] unexpected error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (wishlistId: string, templateName: string, templateId: string) => {
    try {
      setRemovingId(wishlistId);
      const { error } = await supabase
        .from('wishlists')
        .delete()
        .eq('id', wishlistId)
        .eq('user_id', user!.id);

      if (error) {
        console.error('[Wishlist] remove error:', error);
        showError('Failed to remove from wishlist');
        return;
      }

      setWishlistItems((prev) => prev.filter((item) => item.id !== wishlistId));
      trackWishlistRemove(templateId, templateName);
      showSuccess(`"${templateName}" removed from wishlist`);
    } catch (err) {
      console.error('[Wishlist] remove error:', err);
      showError('Failed to remove from wishlist');
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div style={{ background: 'var(--bg-primary)', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <main className="flex-1 pt-16">
        {/* Page header */}
        <div
          className="py-12 border-b"
          style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border)' }}
        >
          <div className="max-w-8xl mx-auto px-6">
            <div className="flex items-center gap-3 mb-2">
              <Icon name="HeartIcon" size={28} style={{ color: 'var(--accent)' } as React.CSSProperties} />
              <h1
                className="font-display font-light text-4xl lg:text-5xl"
                style={{ color: 'var(--text-primary)' }}
              >
                My{' '}
                <span className="italic" style={{ color: 'var(--accent)' }}>
                  Wishlist
                </span>
              </h1>
            </div>
            <p className="text-base" style={{ color: 'var(--text-secondary)' }}>
              {loading ? (
                <span className="inline-block h-5 w-48 rounded animate-pulse" style={{ background: 'var(--bg-card)' }} />
              ) : (
                `${wishlistItems.length} saved template${wishlistItems.length !== 1 ? 's' : ''}`
              )}
            </p>
          </div>
        </div>

        <div className="max-w-8xl mx-auto px-6 py-10">
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {Array.from({ length: 8 }).map((_, i) => (
                <TemplateCardSkeleton key={i} />
              ))}
            </div>
          ) : wishlistItems.length === 0 ? (
            /* Empty State */
            <div className="text-center py-24">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{ background: 'var(--accent-light)' }}
              >
                <Icon name="HeartIcon" size={36} style={{ color: 'var(--accent)' } as React.CSSProperties} />
              </div>
              <h2
                className="font-display font-medium text-2xl mb-3"
                style={{ color: 'var(--text-primary)' }}
              >
                Your wishlist is empty
              </h2>
              <p
                className="text-sm mb-8 max-w-md mx-auto"
                style={{ color: 'var(--text-secondary)' }}
              >
                Save templates you love by clicking the heart icon on any template. They'll appear here for easy access.
              </p>
              <Link href="/templates" className="btn-primary px-8 py-3">
                <Icon name="RectangleStackIcon" size={16} />
                Browse Templates
              </Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {wishlistItems.map((item) => {
                const template = item.template;
                if (!template) return null;

                const platformColor = platformColors[template.platform.toLowerCase()] || '#6B7280';
                const imageUrl = template.thumbnail_url || '/assets/images/no_image.png';

                return (
                  <div
                    key={item.id}
                    className="template-card rounded-2xl overflow-hidden group relative"
                    style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
                  >
                    {/* Remove button */}
                    <button
                      onClick={() => handleRemove(item.id, template.name, template.id)}
                      disabled={removingId === item.id}
                      className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full flex items-center justify-center transition-all"
                      style={{
                        background: 'rgba(239,68,68,0.9)',
                        color: '#FFFFFF',
                        opacity: removingId === item.id ? 0.5 : 1,
                      }}
                      aria-label={`Remove ${template.name} from wishlist`}
                    >
                      {removingId === item.id ? (
                        <Icon name="ArrowPathIcon" size={14} className="animate-spin" />
                      ) : (
                        <Icon name="HeartIcon" size={14} />
                      )}
                    </button>

                    <Link href={`/template-detail?id=${template.id}`}>
                      {/* Image */}
                      <div className="relative aspect-video">
                        <AppImage
                          src={imageUrl}
                          alt={`${template.name} ${template.platform} template preview`}
                          fill
                          className="object-cover"
                        />
                        {template.is_featured && (
                          <span
                            className="absolute top-2 left-2 px-2 py-1 rounded-lg text-2xs font-semibold"
                            style={{ background: 'var(--accent)', color: '#FFFFFF' }}
                          >
                            Featured
                          </span>
                        )}
                      </div>

                      {/* Content */}
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h3
                            className="font-display font-medium text-base"
                            style={{ color: 'var(--text-primary)' }}
                          >
                            {template.name}
                          </h3>
                          <span
                            className="px-2 py-0.5 rounded text-2xs font-semibold flex-shrink-0"
                            style={{
                              background: `${platformColor}18`,
                              color: platformColor,
                            }}
                          >
                            {template.platform.charAt(0).toUpperCase() + template.platform.slice(1)}
                          </span>
                        </div>
                        <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
                          {template.category || 'Uncategorized'}
                        </p>
                        <StarRating rating={template.rating || 0} reviews={template.reviews_count || 0} />
                        <div className="flex items-center justify-between mt-3">
                          <span
                            className="font-display font-semibold text-lg"
                            style={{ color: 'var(--text-primary)' }}
                          >
                            ${template.price}
                          </span>
                          <span className="text-2xs" style={{ color: 'var(--text-muted)' }}>
                            {template.downloads_count || 0} downloads
                          </span>
                        </div>
                      </div>
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
